import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ImageIcon, XIcon } from 'lucide-react';
export interface Slide {
  image?: string;
  caption: string;
}
interface ProjectCarouselProps {
  slides: Slide[];
}
export function ProjectCarousel({
  slides
}: ProjectCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % slides.length);
  };
  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + slides.length) % slides.length);
  };
  const getOffset = (index: number) => {
    const diff = index - currentIndex;
    if (diff === 0) return 0;
    if (diff === 1 || currentIndex === slides.length - 1 && index === 0) return 1;
    if (diff === -1 || currentIndex === 0 && index === slides.length - 1) return -1;
    return diff;
  };
  return <>
      {/* Lightbox */}
      <AnimatePresence data-id="element-44">
        {enlargedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 cursor-pointer" onClick={() => setEnlargedImage(null)} data-id="element-45">
            <button onClick={() => setEnlargedImage(null)} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10" aria-label="Close" data-id="element-46">
              <XIcon className="w-8 h-8" data-id="element-47" />
            </button>
            <motion.img src={enlargedImage} alt="Enlarged view" initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} data-id="element-48" />
          </motion.div>}
      </AnimatePresence>

      {/* Carousel */}
      <div className="relative w-full max-w-7xl mx-auto h-[380px] md:h-[480px] flex items-center justify-center overflow-hidden px-4 md:px-16 py-8" data-id="element-49">
        <div className="relative w-full h-full flex items-center justify-center" data-id="element-50">
          <AnimatePresence initial={false} custom={direction} data-id="element-51">
            {slides.map((slide, index) => {
            const offset = getOffset(index);
            if (Math.abs(offset) > 1) return null;
            return <motion.div key={index} custom={direction} initial={dir => ({
              opacity: 0,
              x: dir > 0 ? '100%' : '-100%',
              scale: 0.8,
              zIndex: 0
            })} animate={{
              opacity: offset === 0 ? 1 : 0.35,
              x: offset === 0 ? '0%' : offset > 0 ? '65%' : '-65%',
              scale: offset === 0 ? 1 : 0.85,
              zIndex: offset === 0 ? 10 : 5
            }} exit={dir => ({
              opacity: 0,
              x: dir > 0 ? '-100%' : '100%',
              scale: 0.8,
              zIndex: 0
            })} transition={{
              duration: 0.5,
              ease: [0.32, 0.72, 0, 1]
            }} className="absolute w-[92%] md:w-[75%] max-w-4xl h-full flex flex-col items-center" data-id="element-52">
                  {/* Image Area */}
                  <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden rounded-2xl" data-id="element-53">
                    {slide.image ? <img src={slide.image} alt={slide.caption} className="max-w-full max-h-full object-contain drop-shadow-lg cursor-pointer hover:opacity-90 transition-opacity" onClick={() => offset === 0 && setEnlargedImage(slide.image!)} data-id="element-54" /> : <div className="flex flex-col items-center justify-center bg-stone-100 rounded-2xl w-full h-full text-stone-400 border border-stone-200" data-id="element-55">
                        <ImageIcon className="w-12 h-12 mb-3 opacity-40" data-id="element-56" />
                        <span className="text-sm font-light tracking-wide" data-id="element-57">
                          Image coming soon
                        </span>
                      </div>}
                  </div>

                  {/* Caption Area - floating white label */}
                  <div className="flex-shrink-0 mt-4" data-id="element-58">
                    <span className="inline-block px-6 py-3 bg-white rounded-xl shadow-md text-stone-800 font-light text-center text-base md:text-lg" data-id="element-59">
                      {slide.caption}
                    </span>
                  </div>
                </motion.div>;
          })}
          </AnimatePresence>
        </div>

        {/* Navigation Controls */}
        <button onClick={prevSlide} className="absolute left-2 md:left-6 z-20 p-3 rounded-full bg-white/90 hover:bg-white shadow-lg text-stone-600 transition-all hover:scale-110 border border-stone-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50" aria-label="Previous slide" data-id="element-60">
          <ChevronLeftIcon className="w-6 h-6" data-id="element-61" />
        </button>
        <button onClick={nextSlide} className="absolute right-2 md:right-6 z-20 p-3 rounded-full bg-white/90 hover:bg-white shadow-lg text-stone-600 transition-all hover:scale-110 border border-stone-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50" aria-label="Next slide" data-id="element-62">
          <ChevronRightIcon className="w-6 h-6" data-id="element-63" />
        </button>
      </div>
    </>;
}